"use client";

import { ArrowUpRight, Check, Clock3, PackageCheck, ShieldCheck } from "lucide-react";
import { useModalStore } from "@/store/store";
import Container from "@/components/Container";
import TopContainer from "@/components/reusable/topContainer";
import { montserrat } from "@/lib/fonts";

const processSteps = [
  {
    number: "01",
    title: "Share your requirement",
    description: "Tell us the size, quantity, use case, and printing needs.",
  },
  {
    number: "02",
    title: "Get an expert recommendation",
    description: "We match the right board, strength, style, and finish.",
  },
  {
    number: "03",
    title: "Receive a clear quote",
    description: "Straightforward pricing and a practical production timeline.",
  },
];

const perks = ["Custom sizes", "Brand printing", "Pan-India delivery"];

const CTABanner = () => {
  const openDoubtModal = useModalStore((state) => state.openDoubtModal);

  return (
    <TopContainer>
      <Container>
        <section className="relative isolate mb-10 overflow-hidden rounded-[28px] bg-[#062f1c] px-5 py-6 shadow-[0_30px_80px_-45px_rgba(6,51,29,0.85)] sm:px-8 sm:py-8 lg:px-10 lg:py-10">
          <div className="absolute -right-24 -top-28 -z-10 h-80 w-80 rounded-full border-[60px] border-white/[0.04]" />
          <div className="absolute -bottom-40 left-[18%] -z-10 h-72 w-72 rounded-full bg-[#8dc4aa]/10 blur-3xl" />
          <div
            className="pointer-events-none absolute inset-0 -z-10 opacity-[0.035]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)",
              backgroundSize: "72px 72px",
            }}
          />

          <div className="grid items-stretch gap-8 lg:grid-cols-[1.08fr_0.92fr] lg:gap-12">
            <div className="flex flex-col justify-between px-1 py-3 sm:px-2 lg:py-5">
              <div>
                <div className="mb-5 flex items-center gap-3">
                  <span className="h-px w-8 bg-[#9bd3b6]" />
                  <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#9bd3b6] sm:text-[11px]">
                    Start your next packaging project
                  </p>
                </div>

                <h2
                  className={`${montserrat.className} max-w-3xl text-[34px] font-semibold leading-[1.08] tracking-[-0.04em] text-white sm:text-[46px] lg:text-[56px]`}
                >
                  Your product deserves a box that
                  <span className="text-[#9bd3b6]"> fits the ambition.</span>
                </h2>

                <p className="mt-5 max-w-xl text-sm leading-7 text-white/55 sm:text-[15px]">
                  Work with our packaging team to create a stronger, smarter corrugated solution—built around your product and your business.
                </p>

                <div className="mt-7 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={openDoubtModal}
                    className="group inline-flex items-center gap-2 rounded-full bg-[#daf7e9] px-6 py-3.5 text-sm font-bold text-[#06331d] shadow-[0_12px_30px_-14px_rgba(218,247,233,0.55)] transition hover:bg-white sm:px-7 sm:py-4"
                  >
                    Plan my packaging
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </button>

                  <span className="inline-flex items-center gap-2 text-xs font-medium text-white/60">
                    <Clock3 className="h-4 w-4 text-[#9bd3b6]" />
                    Quick response. No commitment.
                  </span>
                </div>
              </div>

              <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-3 border-t border-white/10 pt-6">
                {perks.map((perk) => (
                  <li key={perk} className="flex items-center gap-2 text-xs font-medium text-white/65 sm:text-sm">
                    <span className="grid h-5 w-5 place-items-center rounded-full bg-[#9bd3b6]/15 text-[#9bd3b6]">
                      <Check className="h-3 w-3" strokeWidth={2.5} />
                    </span>
                    {perk}
                  </li>
                ))}
              </ul>
            </div>

            <aside className="relative overflow-hidden rounded-2xl border border-white/70 bg-[#f4f8f5] p-6 shadow-[0_24px_60px_-32px_rgba(0,0,0,0.55)] sm:p-8">
              <div className="mb-7 flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#3f6a53]">
                    Simple by design
                  </p>
                  <h3 className={`${montserrat.className} mt-2 text-xl font-semibold tracking-[-0.025em] text-[#092d1c] sm:text-2xl`}>
                    From brief to better box.
                  </h3>
                </div>
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#dcebe2] text-[#0b4a2c]">
                  <PackageCheck className="h-5 w-5" />
                </span>
              </div>

              <ol>
                {processSteps.map((step, index) => (
                  <li key={step.number} className="relative flex gap-4 pb-6 last:pb-0">
                    {index < processSteps.length - 1 && (
                      <span className="absolute bottom-1 left-[15px] top-8 w-px bg-[#0b4a2c]/12" aria-hidden="true" />
                    )}
                    <span className="relative z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full border border-[#0b4a2c]/15 bg-white text-[9px] font-bold text-[#0b4a2c] shadow-sm">
                      {step.number}
                    </span>
                    <div className="pt-0.5">
                      <p className="text-sm font-semibold text-[#153c28]">{step.title}</p>
                      <p className="mt-1 text-xs leading-5 text-[#5a6b62] sm:text-[13px]">{step.description}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="mt-7 flex items-center gap-3 rounded-xl border border-[#0b4a2c]/10 bg-white/75 p-4">
                <ShieldCheck className="h-5 w-5 shrink-0 text-[#39805c]" />
                <p className="text-xs leading-5 text-[#52655a]">
                  Practical advice from packaging specialists, tailored to your requirement.
                </p>
              </div>
            </aside>
          </div>
        </section>
      </Container>
    </TopContainer>
  );
};

export default CTABanner;
